import * as ko from "knockout"
import {IObjectData, IOrganization, IRouteDefinitionParams} from "../../../index";
import {global} from "../../../appContext";
import {_d, getObjectDisplayName, getRawSyncFieldValue} from "../../../util/misc";
import {FormValidator} from "./FormValidator"
import PromiseRequest from "../../../req/PromiseRequest";
import {IPasswordChangeProgressParams} from "../progress";
import {nsPassword} from "../nsPassword";
import {isSilentPasswordExport} from "../isSilentPasswordExport";
import {NOT_FOUND_PREFIX, REJECTED_PREFIX} from "../constants";
import {SyncstepId} from "../../../constants/Syncstep";
import {ObjectTypeId} from "../../../constants";
import {hasher} from 'hasher';
import {trackSurvicateEvent} from "../../../util/survicate";
import detailsHtml from './details.html?raw';

export const template: string = detailsHtml;

// Mirrors the BE stale-password sweep window in
// jsupportclasses.cs / service.asmx.cs (exportpasswordtoqueue). Anything older
// than this on the queue is treated as abandoned and ignored on resume.
const RESUME_MAX_AGE_MS = 12 * 60 * 60 * 1000;

interface IDetailsParams extends IRouteDefinitionParams {
    mode?: TPasswordPageMode
}

export class viewModel {

    private readonly pv = global.permissionValidator
    private readonly mode: TPasswordPageMode
    private readonly isAdminMode: boolean
    private readonly targetUserId: number
    private readonly routeObjectTypeId: ObjectTypeId | undefined

    private readonly errorMessages2Translations: ReadonlyArray<ITranslationObject> = [
        {serverMessage: 'Set password returned wrong number of users', translation: 'password.complexity.fail'}, // todo: dedicated translation key missing
        {serverMessage: 'User does not have sufficient permissions for this operation', translation: 'not.sufficient.permissions'},
        {serverMessage: /New password is to short, minimum length is (\d*)/, translation: 'new.password.too.short'},
        {serverMessage: /New password is to long, maximum length is (\d*)/, translation: 'new.password.too.long'},
        {serverMessage: 'Password cannot contain username', translation: 'password.no.username'},
        {serverMessage: 'Password cannot contain givenname or surname', translation: 'password.no.username'},
        {serverMessage: 'Password does not meet complexity requirements', translation: 'password.complexity.fail'},
        {serverMessage: 'Old password is incorrect', translation: 'old.password.incorrect'},
    ]

    private org: IOrganization = global.login.selectedOrganization() ?? global.login.getHomeOrg()!
    private passwordpolicy = this.org.passwordpolicy
    private formValidator: FormValidator
    // 'checking' is the initial state while resumePollingIfPending() decides
    // whether the user already has a password change in flight. Without it
    // the form would flash on screen for a few hundred ms before being
    // replaced by the polling UI when there's a pending queue row.
    private step: KnockoutObservable<'checking' | 'form' | 'submitting' | 'polling' | 'success' | 'error'> = ko.observable<'checking' | 'form' | 'submitting' | 'polling' | 'success' | 'error'>('checking')
    private welcomeText: string
    private req_changepassword: PromiseRequest = new PromiseRequest('changepassword')
    private req_resetpassword: PromiseRequest = new PromiseRequest('resetpassword')
    private req_getobject: PromiseRequest = global.getPromiseRequest('getobject')
    private errorMessage: KnockoutObservable<string | null> = ko.observable(null)
    private progressParams: KnockoutObservable<IPasswordChangeProgressParams | null> = ko.observable(null)

    private tapEnabled: KnockoutObservable<boolean> = ko.observable(false)
    private hasEntraAnchor: KnockoutObservable<boolean> = ko.observable(false)
    private selectedTab: KnockoutObservable<'password' | 'tap'> = ko.observable<'password' | 'tap'>('password')
    private objectData: KnockoutObservable<IObjectData | null> = ko.observable(null)
    private targetDisplayName: KnockoutObservable<string> = ko.observable('')
    private adminDescriptionKey: KnockoutObservable<string> = ko.observable('password.admin.section.description')
    private req_generatepassword: PromiseRequest = new PromiseRequest('generatepassword')
    private isGenerating: KnockoutObservable<boolean> = ko.observable(false)

    private tooltipOpen: KnockoutObservable<boolean> = ko.observable(false)
    private observableInputs: IObservableInputs = {
        old: ko.observable(''),
        new: ko.observable(''),
        repeat: ko.observable(''),
        toggle_old: ko.observable(true),
        toggle_new: ko.observable(true),
        toggle_repeat: ko.observable(true),
    }

    private isDisposed = false
    private objectContextRetryId: number | null = null

    constructor(params: IDetailsParams = {}) {
        this.mode = params.mode === 'admin' ? 'admin' : 'own'
        this.isAdminMode = this.mode === 'admin'
        this.routeObjectTypeId = params.__objectTypeId ?? params.__routeDef?.objectTypeId
        this.targetUserId = this.resolveTargetUserId(params)

        if (this.isAdminMode) {
            const user = global.login.user();
            if (user && user.id == this.targetUserId) {
                hasher.replaceHash("resetownpassword");
                this.formValidator = new FormValidator({
                    observableInputs: this.observableInputs,
                    passwordPolicy: this.passwordpolicy,
                    mode: this.mode,
                })
                this.welcomeText = ''
                return
            }
        }

        const user = global.login.user();
        const givenName = user ? getRawSyncFieldValue('GivenName', user.syncfields) : '';
        this.welcomeText = `welcome:name|name:${givenName}`
        this.formValidator = new FormValidator({
            observableInputs: this.observableInputs,
            passwordPolicy: this.passwordpolicy,
            mode: this.mode,
        })

        // Resume an in-flight password change if the user navigated away from
        // this page before all targets finished. We also rely on the BE's
        // 12h stale-password sweep so abandoned/orphaned rows from days ago
        // don't trap the user on the polling screen.
        this.resumePollingIfPending()

        document.addEventListener('click', this.onDocumentClick)
    }

    private resolveTargetUserId(params: IDetailsParams): number {
        if (this.mode === 'own') {
            return global.login.user()?.id ?? 0
        }
        const fromRoute = global.getRouteId()
        if (fromRoute) {
            return fromRoute
        }
        return params.__id ?? 0
    }

    /** API users are eAdm-local only — skip export-queue progress polling. */
    private shouldPollExportQueue(): boolean {
        const typeId = this.routeObjectTypeId ?? this.objectData()?.type?.id
        return typeId !== ObjectTypeId.ApiUser
    }

    private readonly onDocumentClick = (e: MouseEvent) => {
        if (this.tooltipOpen()) {
            const wrap = document.querySelector('.export-tooltip-wrap')
            if (wrap && !wrap.contains(e.target as Node)) {
                this.tooltipOpen(false)
            }
        }
    }

    private async loadObjectContext(): Promise<IObjectData | null> {
        if (!this.targetUserId) {
            return null;
        }
        global.cacheManager.removeCache('getobject')
        this.req_getobject.abort()
        const data: IObjectData[] = await this.req_getobject.request(
            {id: this.targetUserId, manual: false},
            false,
            {},
            {useCacheIfAvailable: false},
        )
        const obj = data?.[0] ?? null
        if (obj) {
            this.objectData(obj)
            const displayName = getObjectDisplayName(obj) ?? ''
            this.targetDisplayName(displayName)
            this.adminDescriptionKey(
                displayName
                    ? `password.admin.section.description|name:${displayName}`
                    : 'password.admin.section.description'
            )
            if (this.mode === 'own') {
                this.computeTapEligibility(obj)
            } else {
                this.tapEnabled(false)
                this.hasEntraAnchor(false)
            }
        }
        return obj
    }

    private scheduleObjectContextRetry() {
        if (this.isDisposed || this.objectData()) return
        this.clearObjectContextRetry()
        this.objectContextRetryId = window.setTimeout(async () => {
            this.objectContextRetryId = null
            if (this.isDisposed || this.objectData()) return
            try {
                await this.loadObjectContext()
            } catch (err) {
                _d.warn('Password object load retry failed', err)
            }
        }, 2000)
    }

    private clearObjectContextRetry() {
        if (this.objectContextRetryId !== null) {
            window.clearTimeout(this.objectContextRetryId)
            this.objectContextRetryId = null
        }
    }

    private async resumePollingIfPending() {
        if (!this.targetUserId) {
            if (this.step() === 'checking') {
                this.step('form')
            }
            return;
        }
        const userId = this.targetUserId
        let obj: IObjectData | null = null
        try {
            obj = await this.loadObjectContext()
        } catch (err) {
            _d.warn('Password object load failed', err)
            this.scheduleObjectContextRetry()
        }

        try {
            // Bail out without changing state if the user has already moved
            // off the initial 'checking' screen (e.g. by clicking submit
            // before this resolved). We only ever transition out of
            // 'checking' here.
            if (this.step() !== 'checking') return
            if (!obj || !this.shouldPollExportQueue()) {
                this.step('form')
                return
            }
            const cutoffMs = Date.now() - RESUME_MAX_AGE_MS
            const pending = (obj.exportqueue ?? []).filter(row => {
                if (!row.action || row.action.toLowerCase() !== 'password') return false
                if (row.targetattribute === 'userTAP') return false
                if (isSilentPasswordExport(row)) return false
                if (row.exported) return false
                const ts = nsPassword.parseServerDate(row.registered)
                return ts !== null && ts >= cutoffMs
            })
            if (!pending.length) {
                this.step('form')
                return
            }
            // Use the earliest still-pending row as our submittedAt so the
            // progress component picks up every row from the original flow,
            // not just the ones submitted in the last few seconds.
            let earliest = Number.MAX_SAFE_INTEGER
            pending.forEach(row => {
                const ts = nsPassword.parseServerDate(row.registered)
                if (ts !== null && ts < earliest) {
                    earliest = ts
                }
            })
            const submittedAt = earliest === Number.MAX_SAFE_INTEGER ? new Date() : new Date(earliest)
            this.progressParams({
                userId: userId,
                submittedAt: submittedAt,
                onSucceeded: () => this.step('success'),
                onFailed: (info) => {
                    this.errorMessage(info.lasterror || `password.not.updated`)
                    this.step('error')
                },
            })
            this.step('polling')
        } catch (err) {
            // Resume is a best-effort hint — never block the user from
            // changing their password if the lookup itself fails. Always
            // fall back to the form so the page is interactive.
            _d.warn('Password resume check failed', err)
            if (this.step() === 'checking') {
                this.step('form')
            }
        }
    }

    private static readonly ENTRA_SYNCSTEP_IDS: ReadonlyArray<number> = [
        SyncstepId.Export_Azure,
        SyncstepId.Export_Azure_2,
    ]

    private computeTapEligibility(obj: IObjectData) {
        this.tapEnabled(!!obj.tapenabled)
        const anchor = (obj?.anchor ?? []).some(
            a => !a.deleted && viewModel.ENTRA_SYNCSTEP_IDS.includes(a.sourceid)
        )
        this.hasEntraAnchor(anchor)
    }

    private onSuggestPasswordClick() {
        this.isGenerating(true)
        this.req_generatepassword.request({ nr: 1 })
            .then((data: [string]) => {
                this.observableInputs.new(data[0])
                this.observableInputs.repeat(data[0])
                this.observableInputs.toggle_new(false)
                this.observableInputs.toggle_repeat(false)
            })
            .finally(() => this.isGenerating(false))
    }

    public dispose() {
        this.isDisposed = true
        this.clearObjectContextRetry()
        this.req_changepassword.abort()
        this.req_resetpassword.abort()
        this.req_getobject.abort()
        this.req_generatepassword.abort()
        document.removeEventListener('click', this.onDocumentClick)
    }

    private onSubmitClick() {
        if (!this.targetUserId) {
            return;
        }
        this.step('submitting')
        const submittedAt = new Date()
        const userId = this.targetUserId

        if (this.mode === 'admin') {
            trackSurvicateEvent('ces.password.reset')
            this.req_resetpassword.abort()
            this.req_resetpassword.request({
                id: userId,
                newpassword: this.observableInputs.new(),
            })
            .then(() => this.onSubmitSucceeded(userId, submittedAt))
            .catch((reason: string) => this.onSubmitFailed(reason))
            return
        }

        this.req_changepassword.abort()
        this.req_changepassword.request({
            id: userId,
            newpassword: this.observableInputs.new(),
            oldpassword: this.observableInputs.old(),
        })
        .then(() => this.onSubmitSucceeded(userId, submittedAt))
        .catch((reason: string) => this.onSubmitFailed(reason))
    }

    private onSubmitSucceeded(userId: number, submittedAt: Date) {
        if (!this.shouldPollExportQueue()) {
            this.step('success')
            return
        }
        this.progressParams({
            userId: userId,
            submittedAt: submittedAt,
            onSucceeded: () => this.step('success'),
            onFailed: (info) => {
                this.errorMessage(info.lasterror || `password.not.updated`)
                this.step('error')
            },
        })
        this.step('polling')
    }

    private onSubmitFailed(reason: string) {
        // Real-time Entra rejection bubbles up from the API as "Rejected: ..." or "Not found: ...".
        // Show the verbatim message instead of mapping to a translation key.
        if (typeof reason === 'string' && (reason.startsWith(REJECTED_PREFIX) || reason.startsWith(NOT_FOUND_PREFIX))) {
            this.errorMessage(reason)
        } else {
            const rawTranslation = this.errorReason2Translation(reason)
            this.errorMessage(rawTranslation)
        }
        this.step('error')
    }

    private errorReason2Translation(reason: string): TErrorTranslation | string {
        // 'new.password.too.long|num:128'
        const item = this.errorMessages2Translations.find(translationObject => {
            if (typeof translationObject.serverMessage === 'string') {
                return translationObject.serverMessage == reason
            } else {
                return translationObject.serverMessage.test(reason)
            }
        })
        if (!item) {
            return reason
        }

        if (typeof item.serverMessage === 'string') {
            return item.translation
        } else {
            item.serverMessage.lastIndex = 0
            const regExpResult = item.serverMessage.exec(reason)
            const num = regExpResult?.[1] ?? ''
            return item.translation + '|num:' + num
        }

    }

}
